/**
 * Promotional popup: when to show it, and when to keep quiet.
 *
 * Timing follows what actually converts rather than what is easiest:
 *   - never straight away; an instant popup is dismissed reflexively and
 *     pushes bounce rate up,
 *   - it opens on whichever comes first: a real dwell (default 7s) or a
 *     meaningful scroll (default 35% of the page),
 *   - on desktop it also opens on exit intent, when the pointer leaves
 *     towards the browser chrome,
 *   - once per session, and once dismissed it stays away for days.
 *
 * It never appears on the cart or checkout, where the shopper already has
 * intent and an interruption only costs money.
 *
 * Memory is localStorage, first-party, no tracking and nothing personal:
 * a single timestamp saying "do not ask again until".
 */

const KEY = 'imagine:promo-popup:snooze-until';
const SESSION_KEY = 'imagine:promo-popup:seen';

/** localStorage throws in private mode and when site data is blocked. */
const store = {
  get(key, fallback = null) {
    try {
      return window.localStorage.getItem(key) ?? fallback;
    } catch {
      return fallback;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      /* nothing to do: the popup simply forgets */
    }
  },
};

const root = document.querySelector('[data-promo-popup]');

if (root) {
  const delay = Number(root.dataset.delay) || 7000;
  const scrollTrigger = Number(root.dataset.scroll) || 35;
  const snoozeDays = Number(root.dataset.rememberDays) || 7;
  const convertedDays = Number(root.dataset.rememberDaysConverted) || 30;

  const path = window.location.pathname;
  const onCheckoutPath = /\/(cart|checkout)(\/|$)/.test(path);
  const snoozedUntil = Number(store.get(KEY, 0));
  const snoozed = Number.isFinite(snoozedUntil) && Date.now() < snoozedUntil;
  const seenThisSession = sessionStorage.getItem?.(SESSION_KEY) === '1';

  let opened = false;
  let timer = null;

  const snooze = (days) => {
    store.set(KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));
  };

  const close = () => {
    root.hidden = true;
    root.classList.remove('promo-popup--open');
    document.documentElement.classList.remove('promo-popup-locked');
    snooze(snoozeDays);
    document.removeEventListener('keydown', onKey);
  };

  function onKey(event) {
    if (event.key === 'Escape') close();
  }

  const open = () => {
    if (opened) return;
    opened = true;
    window.clearTimeout(timer);
    detach();

    root.hidden = false;
    // next frame, so the transition has a start state to animate from
    requestAnimationFrame(() => root.classList.add('promo-popup--open'));
    document.documentElement.classList.add('promo-popup-locked');
    document.addEventListener('keydown', onKey);

    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      /* ignore */
    }

    root.querySelector('.promo-popup__cta')?.focus({ preventScroll: true });
  };

  const onScroll = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollable <= 0) return;
    const percent = (window.scrollY / scrollable) * 100;
    if (percent >= scrollTrigger) open();
  };

  const onExitIntent = (event) => {
    if (event.clientY <= 0) open();
  };

  function detach() {
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('mouseout', onExitIntent);
  }

  if (!onCheckoutPath && !snoozed && !seenThisSession) {
    timer = window.setTimeout(open, delay);
    window.addEventListener('scroll', onScroll, { passive: true });

    // Exit intent is a pointer gesture; it has no meaning on touch.
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.addEventListener('mouseout', onExitIntent);
    }
  }

  root.querySelectorAll('[data-promo-close]').forEach((el) => {
    el.addEventListener('click', close);
  });

  /* ---------------------------------------------------------------
     Signup. Klaviyo is the SMS platform already installed on this
     store, so the phone goes straight into it with explicit SMS
     consent - which is what lets a Klaviyo flow text the discount
     code back within seconds.

     The list id is set in snippets/promo-popup.liquid. Without it
     Klaviyo cannot record consent, so we say so rather than show a
     success message for something that did not happen.
     --------------------------------------------------------------- */
  const form = root.querySelector('[data-promo-form]');
  const status = root.querySelector('[data-promo-status]');
  const successText = root.querySelector('[data-promo-success]')?.innerHTML.trim() ?? '';
  const company = root.dataset.klaviyoCompany;
  const list = root.dataset.klaviyoList;

  const say = (message, kind) => {
    if (!status) return;
    status.textContent = message;
    status.dataset.kind = kind;
  };

  /** Israeli mobile numbers, typed the way people actually type them. */
  const toE164 = (raw) => {
    const digits = raw.replace(/[^\d+]/g, '');
    if (/^\+972\d{8,9}$/.test(digits)) return digits;
    if (/^972\d{8,9}$/.test(digits)) return `+${digits}`;
    if (/^0\d{8,9}$/.test(digits)) return `+972${digits.slice(1)}`;
    return null;
  };

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const nameInput = form.querySelector('input[name="name"]');
    const phoneInput = form.querySelector('input[name="phone"]');
    const consent = form.querySelector('input[name="consent"]');

    const fullName = (nameInput?.value ?? '').trim();
    if (fullName.length < 2) {
      say('נא להזין שם מלא', 'error');
      nameInput?.focus();
      return;
    }

    const phone = toE164(phoneInput?.value ?? '');
    if (!phone) {
      say('נא להזין מספר טלפון נייד תקין', 'error');
      phoneInput?.focus();
      return;
    }
    if (!consent?.checked) {
      say('צריך לאשר קבלת הודעות כדי להצטרף', 'error');
      return;
    }
    if (!company || !list) {
      say('ההרשמה עדיין לא מחוברת. נא להשלים את מזהה הרשימה בקלביו.', 'error');
      return;
    }

    const button = form.querySelector('button[type="submit"]');
    button?.setAttribute('disabled', '');
    say('רק רגע...', 'pending');

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(company)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      phone_number: phone,
                      first_name: fullName.split(/\s+/)[0],
                      last_name: fullName.split(/\s+/).slice(1).join(' '),
                      subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } },
                    },
                  },
                },
              },
              relationships: { list: { data: { type: 'list', id: list } } },
            },
          }),
        }
      );

      if (!response.ok) throw new Error(String(response.status));
      say(successText, 'success');
      snooze(convertedDays);
      form.querySelector('.promo-popup__input')?.setAttribute('hidden', '');
      form.querySelector('.promo-popup__consent')?.setAttribute('hidden', '');
      button?.setAttribute('hidden', '');
    } catch {
      say('משהו השתבש. אפשר לנסות שוב בעוד רגע.', 'error');
      button?.removeAttribute('disabled');
    }
  });
}
